import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { Button } from './ui/button';
import { Label } from './ui/label';
import toast from 'react-hot-toast';

const SPORTS = [
  { value: 'cricket', label: '🏏 Cricket' },
  { value: 'football', label: '⚽ Football (Soccer)' },
  { value: 'basketball', label: '🏀 Basketball' },
  { value: 'tennis', label: '🎾 Tennis' },
  { value: 'baseball', label: '⚾ Baseball' },
  { value: 'nfl', label: '🏈 American Football (NFL)' },
  { value: 'nhl', label: '🏒 Ice Hockey (NHL)' },
  { value: 'rugby', label: '🏉 Rugby' },
];

function ComparisonForm({ onCompare, loading }) {
  const [sport, setSport] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [player1, setPlayer1] = useState('');
  const [player2, setPlayer2] = useState('');

  // Fetch categories when sport changes
  useEffect(() => {
    const fetchCategories = async () => {
      if (sport) {
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          const response = await fetch(`${API_URL}/api/sports/categories/${sport}`);
          if (response.ok) {
            const data = await response.json();
            setCategories(data.categories || []);
            // Reset category when sport changes
            setCategory('');
          } else {
            setCategories([]);
          }
        } catch (error) {
          console.error('Error fetching categories:', error);
          setCategories([]);
        }
      } else {
        setCategories([]);
        setCategory('');
      }
    };

    fetchCategories();
  }, [sport]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!sport) {
      toast.error('Please select a sport first');
      return;
    }
    
    if (!player1.trim() || !player2.trim()) {
      toast.error('Please enter both player names');
      return;
    }

    // Show info toast about same sport requirement
    const sportLabel = SPORTS.find(s => s.value === sport)?.label || sport;
    toast(
      `Comparing players in ${sportLabel}\nPlease ensure both players are from the same sport.`,
      {
        icon: 'ℹ️',
        duration: 3000,
      }
    );

    onCompare(sport, player1.trim(), player2.trim(), category || null);
  };

  return (
    <Card className="w-full shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl">Compare Players</CardTitle>
        <CardDescription>
          Select a sport and enter two player names from the same sport to start the comparison
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="sport">Select Sport</Label>
            <Select
              id="sport"
              value={sport}
              onChange={(e) => {
                setSport(e.target.value);
                // Reset players when sport changes to avoid confusion
                setPlayer1('');
                setPlayer2('');
              }}
              required
              disabled={loading}
            >
              <option value="">Choose a sport...</option>
              {SPORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>

          {categories.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="category">Select Format/Category</Label>
              <Select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
              >
                <option value="">All Formats (Career)</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="player1">Player 1</Label>
              <Input
                id="player1"
                type="text"
                value={player1}
                onChange={(e) => setPlayer1(e.target.value)}
                placeholder={`Enter ${sport ? SPORTS.find(s => s.value === sport)?.label.split(' ')[1] || 'player' : 'player'} name`}
                required
                disabled={loading}
              />
            </div>

            <div className="hidden md:flex items-center justify-center h-10 px-4">
              <span className="text-2xl font-bold text-primary">VS</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="player2">Player 2</Label>
              <Input
                id="player2"
                type="text"
                value={player2}
                onChange={(e) => setPlayer2(e.target.value)}
                placeholder={`Enter ${sport ? SPORTS.find(s => s.value === sport)?.label.split(' ')[1] || 'player' : 'player'} name`}
                required
                disabled={loading}
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            size="lg"
            disabled={loading}
          >
            {loading ? 'Comparing...' : 'Compare Players'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default ComparisonForm;
